<script setup lang="ts">
import { computed } from 'vue';
import BaseButton from './BaseButton.vue';

interface ButtonConfig {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

interface Props {
  icon?: string;
  iconClass?: string;
  title: string;
  message: string;
  primaryButton?: ButtonConfig;
  secondaryButton?: ButtonConfig;
  size?: 'default' | 'compact';
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  iconClass: undefined,
  primaryButton: undefined,
  secondaryButton: undefined,
  size: 'default',
});

const containerClass = computed(() => {
  const baseClass = 'flex flex-col items-center justify-center px-4 text-center';
  const spacingClass = props.size === 'compact' ? 'py-8' : 'py-20';
  return `${baseClass} ${spacingClass}`;
});
</script>

<template>
  <div :class="containerClass">
    <div v-if="icon" class="mb-6">
      <i :class="['w-20 h-20 opacity-70 icon', icon, iconClass]" aria-hidden="true"></i>
    </div>

    <h2 class="text-h3 mb-4 text-text-primary">
      {{ title }}
    </h2>

    <p class="text-body mb-8 max-w-md text-text-primary opacity-70">
      {{ message }}
    </p>

    <div v-if="secondaryButton || primaryButton" class="flex gap-4">
      <BaseButton
        v-if="secondaryButton"
        variant="secondary"
        :disabled="secondaryButton.disabled"
        :loading="secondaryButton.loading"
        @click="secondaryButton.onClick"
      >
        {{ secondaryButton.text }}
      </BaseButton>
      <BaseButton
        v-if="primaryButton"
        variant="primary"
        :disabled="primaryButton.disabled"
        :loading="primaryButton.loading"
        @click="primaryButton.onClick"
      >
        {{ primaryButton.text }}
      </BaseButton>
    </div>
  </div>
</template>
